import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "@/App";
import api from "@/lib/api";
import {
  Shield, Download, FileText, ArrowLeft, Bird, Lock, Eye,
  ToggleRight, Check, Loader2, ExternalLink, Mail
} from "lucide-react";

export default function GDPRPage() {
  const nav = useNavigate();
  const { auth } = useAppContext();
  const [policyData, setPolicyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    (async () => {
      try {
        const data = await api.getGdprPolicy();
        setPolicyData(data);
      } catch (e) { void 0; }
      setLoading(false);
    })();
  }, []);

  const TABS = [
    { id: "overview", label: "Overview", icon: Shield },
    { id: "data", label: "Data We Collect", icon: FileText },
    { id: "rights", label: "Your Rights", icon: Lock },
    { id: "cookies", label: "Cookies", icon: Eye },
    { id: "third-parties", label: "Third Parties", icon: ExternalLink },
    { id: "contact", label: "Contact DPO", icon: Mail },
  ];

  return (
    <div className="min-h-[100dvh] bg-white" data-testid="gdpr-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => nav(-1)}
              className="p-2 rounded-xl bg-slate-100 text-slate-500 hover:text-slate-900 hover:bg-slate-200 transition-all"
              data-testid="gdpr-back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="w-9 h-9 bg-lime-500 rounded-xl flex items-center justify-center">
              <Bird className="w-4.5 h-4.5 text-slate-950" strokeWidth={2} />
            </div>
            <div>
              <h1 className="text-base font-black tracking-tighter uppercase text-slate-900">Privacy & GDPR</h1>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Pigeon Supply Data Protection</p>
            </div>
          </div>
          {auth?.jwt && (
            <button
              onClick={() => nav("/privacy")}
              className="px-4 py-2 bg-lime-50 hover:bg-lime-100 border border-lime-200 text-lime-700 rounded-xl text-xs font-bold transition-colors flex items-center gap-2"
              data-testid="gdpr-manage-btn"
            >
              <ToggleRight className="w-3.5 h-3.5" /> Manage My Data
            </button>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-lime-50 border-2 border-lime-200 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <Shield className="w-8 h-8 text-lime-500" />
          </div>
          <h2 className="text-2xl md:text-3xl font-black tracking-tighter uppercase text-slate-900 mb-3">
            Your Privacy Matters
          </h2>
          <p className="text-sm text-slate-500 max-w-lg mx-auto leading-relaxed">
            Pigeon Supply is fully compliant with the EU General Data Protection Regulation (GDPR).
            We collect only what's necessary and give you full control over your data.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-8 justify-center" data-testid="gdpr-tabs">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`gdpr-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${
                  activeTab === tab.id
                    ? "bg-slate-900 text-white shadow-sm"
                    : "bg-slate-50 text-slate-500 border border-slate-200 hover:border-slate-300"
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {tab.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 text-lime-500 animate-spin" />
          </div>
        ) : (
          <div className="max-w-3xl mx-auto">
            {/* Overview Tab */}
            {activeTab === "overview" && (
              <div className="space-y-5" data-testid="gdpr-overview">
                <InfoCard title="Data Controller" value="Pigeon Supply" sub={policyData?.contact_email || "privacy@onepigeon.app"} />
                <InfoCard title="Legal Basis" value={policyData?.legal_basis || "Contract performance (Art. 6(1)(b))"} />
                <InfoCard title="Data Protection Officer" value={policyData?.data_protection_officer || "privacy@onepigeon.app"} />
                <div className="p-5 bg-lime-50 border border-lime-200 rounded-xl">
                  <h3 className="text-xs font-bold text-lime-700 uppercase tracking-widest mb-2">Key Principles</h3>
                  <ul className="space-y-2">
                    {[
                      "We collect only the minimum data necessary",
                      "No tracking cookies or advertising identifiers",
                      "Voice data is processed locally — never stored on our servers",
                      "Payment processing is handled by Stripe (PCI DSS compliant)",
                      "You can export or delete all your data at any time",
                      "Activity logs are auto-purged after 365 days",
                    ].map((item, i) => (
                      <li key={item.slice(0, 30)} className="flex items-start gap-2 text-xs text-lime-800">
                        <Check className="w-3.5 h-3.5 text-lime-500 mt-0.5 shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Data Collection Tab */}
            {activeTab === "data" && (
              <div className="space-y-4" data-testid="gdpr-data">
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider mb-1">Data We Collect</h3>
                <div className="space-y-2">
                  {(policyData?.data_collected || []).map((item, i) => (
                    <div key={`collected-${i}-${item?.slice?.(0,20)}`} className="p-4 bg-white border border-slate-200 rounded-xl flex items-start gap-3">
                      <span className="w-6 h-6 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-center shrink-0 text-blue-500 text-[10px] font-black">{i + 1}</span>
                      <p className="text-xs text-slate-700 leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider mt-6 mb-1">Data We Do NOT Collect</h3>
                <div className="space-y-2">
                  {(policyData?.data_not_collected || []).map((item, i) => (
                    <div key={`not-collected-${i}-${item?.slice?.(0,20)}`} className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex items-center gap-3">
                      <span className="w-5 h-5 bg-red-50 border border-red-200 rounded-lg flex items-center justify-center shrink-0 text-red-400 text-[10px] font-black">X</span>
                      <p className="text-xs text-slate-600">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Rights Tab */}
            {activeTab === "rights" && (
              <div className="space-y-4" data-testid="gdpr-rights">
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider mb-1">Your Rights Under GDPR</h3>
                {[
                  { title: "Right of Access (Art. 15)", desc: "Request a copy of all personal data we hold about you.", action: "GET /api/gdpr/my-data" },
                  { title: "Right to Data Portability (Art. 20)", desc: "Export all your data in a machine-readable JSON format.", action: "GET /api/gdpr/my-data" },
                  { title: "Right to Erasure (Art. 17)", desc: "Permanently delete your account and all personal data. Hub operational data is anonymized.", action: "DELETE /api/gdpr/account" },
                  { title: "Right to Withdraw Consent (Art. 7)", desc: "Withdraw any consent at any time via Privacy Settings.", action: "POST /api/gdpr/consent" },
                  { title: "Right to Rectification (Art. 16)", desc: "Update your personal information through your account settings." },
                  { title: "Right to Object (Art. 21)", desc: "Object to processing of your personal data for specific purposes." },
                ].map((right, i) => (
                  <div key={right.title} className="p-5 bg-white border border-slate-200 rounded-xl">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h4 className="text-xs font-bold text-slate-900 mb-1">{right.title}</h4>
                        <p className="text-[11px] text-slate-500 leading-relaxed">{right.desc}</p>
                      </div>
                      {right.action && (
                        <code className="text-[9px] text-slate-400 bg-slate-50 px-2 py-1 rounded-lg font-mono shrink-0 border border-slate-200">{right.action}</code>
                      )}
                    </div>
                  </div>
                ))}
                {auth?.jwt && (
                  <button
                    onClick={() => nav("/privacy")}
                    className="w-full py-4 bg-lime-500 hover:bg-lime-400 text-slate-950 rounded-xl text-sm font-black uppercase flex items-center justify-center gap-2"
                    data-testid="gdpr-exercise-rights-btn"
                  >
                    <Lock className="w-4 h-4" /> Exercise Your Rights
                  </button>
                )}
              </div>
            )}

            {/* Cookies Tab */}
            {activeTab === "cookies" && (
              <div className="space-y-4" data-testid="gdpr-cookies">
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider mb-1">Cookie Policy</h3>
                <div className="p-5 bg-lime-50 border border-lime-200 rounded-xl">
                  <p className="text-xs text-lime-800 font-medium mb-3">Pigeon Supply uses only strictly necessary cookies:</p>
                  <ul className="space-y-2">
                    {[
                      { name: "pigeon-auth", purpose: "Authentication token (JWT)", duration: "72 hours" },
                      { name: "pigeon-session", purpose: "Current hub session state", duration: "Session" },
                      { name: "pigeon-lang", purpose: "Language preference", duration: "Persistent" },
                      { name: "pigeon-cookie-consent", purpose: "Cookie consent status", duration: "Persistent" },
                    ].map((cookie, i) => (
                      <li key={cookie.name} className="flex items-center justify-between p-3 bg-white rounded-xl border border-lime-100">
                        <div>
                          <code className="text-[10px] font-mono font-bold text-slate-700">{cookie.name}</code>
                          <p className="text-[10px] text-slate-500 mt-0.5">{cookie.purpose}</p>
                        </div>
                        <span className="text-[9px] font-bold bg-slate-100 text-slate-500 px-2 py-1 rounded-full">{cookie.duration}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                  <h4 className="text-xs font-bold text-red-700 mb-2">We Do NOT Use:</h4>
                  <ul className="space-y-1.5">
                    {["Third-party tracking cookies", "Advertising cookies or pixels", "Analytics cookies (analytics are server-side, anonymized)", "Social media tracking"].map((item, i) => (
                      <li key={item} className="text-[11px] text-red-600 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-red-400 rounded-full" /> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Third Parties Tab */}
            {activeTab === "third-parties" && (
              <div className="space-y-4" data-testid="gdpr-third-parties">
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider mb-1">Third-Party Processors</h3>
                {(policyData?.third_parties || []).map((tp, i) => (
                  <div key={tp.name || `tp-${i}`} className="p-5 bg-white border border-slate-200 rounded-xl flex items-center justify-between">
                    <div>
                      <h4 className="text-xs font-bold text-slate-900">{tp.name}</h4>
                      <p className="text-[10px] text-slate-500 mt-0.5">{tp.purpose}</p>
                    </div>
                    <span className={`text-[9px] font-black px-2.5 py-1 rounded-full ${tp.gdpr_compliant ? "bg-lime-100 text-lime-600" : "bg-red-100 text-red-600"}`}>
                      {tp.gdpr_compliant ? "GDPR COMPLIANT" : "REVIEW"}
                    </span>
                  </div>
                ))}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                  <h4 className="text-xs font-bold text-slate-700 mb-2">Data Processing Agreements</h4>
                  <p className="text-[11px] text-slate-500 leading-relaxed">
                    Pigeon Supply has Data Processing Agreements (DPAs) in place with all third-party processors. 
                    All processors are GDPR-compliant and data is processed within the EU or under adequate safeguards.
                  </p>
                </div>
              </div>
            )}

            {/* Contact Tab */}
            {activeTab === "contact" && (
              <div className="space-y-4" data-testid="gdpr-contact">
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider mb-1">Data Protection Officer</h3>
                <div className="p-6 bg-white border border-slate-200 rounded-xl text-center">
                  <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-6 h-6 text-slate-500" />
                  </div>
                  <h4 className="text-sm font-bold text-slate-900 mb-1">Contact our DPO</h4>
                  <p className="text-xs text-slate-500 mb-4">For any privacy-related inquiries or to exercise your rights</p>
                  <a
                    href="mailto:privacy@onepigeon.app"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition-colors"
                    data-testid="gdpr-contact-email"
                  >
                    <Mail className="w-3.5 h-3.5" /> privacy@onepigeon.app
                  </a>
                  <p className="text-[10px] text-slate-400 mt-4">We respond to all privacy requests within 30 days as required by GDPR.</p>
                </div>
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <h4 className="text-xs font-bold text-amber-700 mb-1">Supervisory Authority</h4>
                  <p className="text-[11px] text-amber-600 leading-relaxed">
                    You have the right to lodge a complaint with your local data protection authority.
                    In Belgium, this is the Gegevensbeschermingsautoriteit (GBA) — contact@apd-gba.be
                  </p>
                </div>
              </div>
            )}

            {/* Retention Policy */}
            <div className="mt-10 p-5 bg-slate-50 border border-slate-200 rounded-xl">
              <h3 className="text-xs font-bold text-slate-700 uppercase tracking-widest mb-3">Data Retention Policy</h3>
              <div className="space-y-2">
                {Object.entries(policyData?.retention_policy || {}).map(([key, val]) => (
                  <div key={key} className="flex justify-between items-center py-1.5 border-b border-slate-100 last:border-0">
                    <span className="text-[10px] font-bold text-slate-500 uppercase">{key.replace(/_/g, " ")}</span>
                    <span className="text-[10px] text-slate-400">{val}</span>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-center text-[10px] text-slate-400 mt-8">
              Last updated: February 2026 &middot; Pigeon Supply &middot; GDPR Art. 13-14 compliant
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

function InfoCard({ title, value, sub }) {
  return (
    <div className="p-4 bg-white border border-slate-200 rounded-xl">
      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">{title}</p>
      <p className="text-sm font-bold text-slate-900">{value}</p>
      {sub && <p className="text-[10px] text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}
